import { useState, useEffect, useMemo } from 'react'
import { Alert, Badge, Button, Modal, Spinner, Table } from 'react-bootstrap'
import api from '../../services/api'
import { formatMoneyDisplay } from '../../utils/moneyDecimals'
import { useAuth } from '../../hooks/useAuth'
import './BalanzaComprobacionTable.css'

const formatBalanzaAmount = (value) => formatMoneyDisplay(value, 'MXN', [], { prefix: '' })

const AccountDrilldownTable = ({
  cuentas = [],
  totales = null,
  cuentasReportadas = null,
  clientId,
  clientName,
  startDate,
  endDate,
  reportCacheKey,
}) => {
  const { user } = useAuth()
  const [expandedMovimientos, setExpandedMovimientos] = useState({})
  const [movimientosByAccount, setMovimientosByAccount] = useState({})
  const [movimientosErrorByAccount, setMovimientosErrorByAccount] = useState({})
  const [loadingMovimientos, setLoadingMovimientos] = useState({})
  const [xmlModal, setXmlModal] = useState({ show: false, loading: false, data: null, error: '' })

  const impresionFecha = useMemo(
    () => new Date().toLocaleString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).replace(',', ''),
    // reportCacheKey no se lee adentro: es el disparador de recalculo cuando se
    // recarga el reporte. Sin el, la marca de tiempo se congela en el primer render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reportCacheKey]
  )

  const usuarioLabel = useMemo(() => {
    if (!user) return '—'
    const full = `${user.first_name || ''} ${user.last_name || ''}`.trim()
    return full || user.username || '—'
  }, [user])

  useEffect(() => {
    setExpandedMovimientos({})
    setMovimientosByAccount({})
    setMovimientosErrorByAccount({})
    setLoadingMovimientos({})
  }, [clientId, startDate, endDate, reportCacheKey])

  const fetchMovimientos = async (accountId) => {
    setLoadingMovimientos(prev => ({ ...prev, [accountId]: true }))
    try {
      const params = new URLSearchParams()
      if (clientId) params.append('client_id', clientId)
      if (startDate) params.append('start_date', startDate)
      if (endDate) params.append('end_date', endDate)
      params.append('account_id', accountId)
      const res = await api.get(`/financial/reportes/balanza-movimientos-cuenta/?${params.toString()}`)
      setMovimientosErrorByAccount((prev) => {
        const next = { ...prev }
        delete next[accountId]
        return next
      })
      setMovimientosByAccount(prev => ({ ...prev, [accountId]: res.data?.movimientos || [] }))
    } catch (e) {
      const st = e.response?.status
      const msg =
        st === 404
          ? 'El endpoint de movimientos no está disponible (404).'
          : String(
              e.response?.data?.detail ||
                e.response?.data?.error ||
                e.message ||
                'Error al cargar movimientos'
            )
      setMovimientosErrorByAccount((prev) => ({ ...prev, [accountId]: msg }))
      setMovimientosByAccount((prev) => ({ ...prev, [accountId]: [] }))
    } finally {
      setLoadingMovimientos(prev => ({ ...prev, [accountId]: false }))
    }
  }

  const toggleMovimientos = async (account) => {
    const accountId = account.id
    const willExpand = !expandedMovimientos[accountId]
    setExpandedMovimientos(prev => ({ ...prev, [accountId]: willExpand }))
    if (willExpand && movimientosByAccount[accountId] === undefined) {
      await fetchMovimientos(accountId)
    }
  }

  const openXmlModal = async (txId) => {
    setXmlModal({ show: true, loading: true, data: null, error: '' })
    try {
      const res = await api.get(`/financial/reportes/balanza-transaccion-xml/?transaction_id=${txId}`)
      setXmlModal({ show: true, loading: false, data: res.data, error: '' })
    } catch (e) {
      const err = e.response?.data?.error || 'No se pudieron cargar los metadatos del XML'
      setXmlModal({ show: true, loading: false, data: null, error: err })
    }
  }

  const downloadXml = (txId) => {
    const url = `/financial/reportes/balanza-transaccion-xml/?transaction_id=${txId}&download=true`
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const collapseMovimientos = () => {
    setExpandedMovimientos({})
    setMovimientosByAccount({})
    setMovimientosErrorByAccount({})
    setLoadingMovimientos({})
  }

  const isGroupGap = (index) => {
    if (index <= 0) return false
    const curr = cuentas[index]
    const prev = cuentas[index - 1]
    return !!curr?.has_children && !prev?.has_children
  }

  const toAmount = (value) => {
    const n = parseFloat(value)
    return Number.isFinite(n) ? n : 0
  }

  const periodoLabel = (startDate && endDate)
    ? `entre ${startDate} y ${endDate}`
    : 'del período seleccionado'

  const renderEmptyMovimientosMessage = (cuenta) => {
    const saldoInicial = toAmount(cuenta.saldo_inicial)
    const debe = toAmount(cuenta.debe)
    const haber = toAmount(cuenta.haber)
    const saldoFinal = toAmount(cuenta.saldo_final)
    const sinSaldos = saldoInicial === 0 && debe === 0 && haber === 0 && saldoFinal === 0

    if (sinSaldos) {
      return (
        <div className="py-2 text-muted small balanza-movs-empty-hint">
          Sin movimientos ni saldos {periodoLabel}.
        </div>
      )
    }

    if (debe === 0 && haber === 0) {
      return (
        <div className="py-2 text-muted small balanza-movs-empty-hint">
          <div>Sin movimientos {periodoLabel}.</div>
          <div className="mt-1">
            El saldo de la cuenta proviene de <strong>períodos anteriores</strong>
            {saldoInicial !== 0 && (
              <> (saldo inicial: <strong>{formatBalanzaAmount(saldoInicial)}</strong>)</>
            )}
            {saldoFinal !== 0 && saldoFinal !== saldoInicial && (
              <>; saldo final: <strong>{formatBalanzaAmount(saldoFinal)}</strong></>
            )}
            .
          </div>
        </div>
      )
    }

    return (
      <div className="py-2 text-muted small balanza-movs-empty-hint">
        <div>Sin movimientos {periodoLabel}.</div>
        <div className="mt-1">
          Saldos en balanza: inicial <strong>{formatBalanzaAmount(saldoInicial)}</strong>,
          debe <strong>{formatBalanzaAmount(debe)}</strong>,
          haber <strong>{formatBalanzaAmount(haber)}</strong>,
          final <strong>{formatBalanzaAmount(saldoFinal)}</strong>.
        </div>
      </div>
    )
  }

  const renderMovimientosRow = (cuenta) => {
    const accountId = cuenta.id
    if (!expandedMovimientos[accountId]) return null

    const movimientos = movimientosByAccount[accountId] || []
    const movErr = movimientosErrorByAccount[accountId]

    return (
      <tr key={`movs-${accountId}`}>
        <td colSpan={6} className="p-0 border-0">
          <div className="balanza-movs-panel">
            {loadingMovimientos[accountId] ? (
              <div className="py-2"><Spinner size="sm" /> Cargando movimientos...</div>
            ) : movErr ? (
              <Alert variant="warning" className="py-2 mb-0 small">{movErr}</Alert>
            ) : movimientos.length === 0 ? (
              renderEmptyMovimientosMessage(cuenta)
            ) : (
              <Table size="sm" className="mb-0 bg-white">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Referencia</th>
                    <th>Descripción</th>
                    <th className="text-end">Debe</th>
                    <th className="text-end">Haber</th>
                    <th>XML</th>
                  </tr>
                </thead>
                <tbody>
                  {movimientos.map((m) => (
                    <tr key={`tx-${m.id}`}>
                      <td>{m.date}</td>
                      <td>{m.reference || '—'}</td>
                      <td>{m.description || '—'}</td>
                      <td className="text-end">{formatBalanzaAmount(m.debit || 0)}</td>
                      <td className="text-end">{formatBalanzaAmount(m.credit || 0)}</td>
                      <td>
                        <Button size="sm" variant="link" className="p-0" onClick={() => openXmlModal(m.id)}>
                          Ver XML
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </td>
      </tr>
    )
  }

  const totalCuentas = cuentasReportadas ?? cuentas.length

  return (
    <div className="balanza-report-wrap">
      <div className="balanza-toolbar">
        <span>Cliente: <strong>{clientName || (clientId ? `#${clientId}` : 'N/A')}</strong></span>
        <span>|</span>
        <span>Periodo: <strong>{startDate || '—'} a {endDate || '—'}</strong></span>
        <Button size="sm" variant="link" className="p-0 ms-auto" onClick={collapseMovimientos}>
          Colapsar movimientos
        </Button>
      </div>

      <div className="balanza-moneda-line">Tipos de monedas: MXN</div>

      <div className="table-responsive">
        <table className="balanza-table">
          <thead>
            <tr>
              <th style={{ width: '14%' }}>No. de cuenta</th>
              <th style={{ width: '34%' }}>Descripción</th>
              <th className="balanza-num" style={{ width: '13%' }}>Saldo inicial</th>
              <th className="balanza-num" style={{ width: '13%' }}>Debe</th>
              <th className="balanza-num" style={{ width: '13%' }}>Haber</th>
              <th className="balanza-num" style={{ width: '13%' }}>Saldo final</th>
            </tr>
          </thead>
          <tbody>
            {cuentas.flatMap((cuenta, index) => {
              const nivel = parseInt(cuenta.nivel, 10) || 1
              const indentPx = Math.max(0, nivel - 1) * 16
              const isMayor = !!cuenta.has_children
              const name = isMayor ? (cuenta.name || '').toUpperCase() : (cuenta.name || '')
              const rowClass = [
                isGroupGap(index) ? 'balanza-group-gap' : '',
                isMayor ? 'balanza-row-mayor' : '',
              ].filter(Boolean).join(' ')

              const rows = [
                <tr key={`acc-${cuenta.id}`} className={rowClass || undefined}>
                  <td className="balanza-cuenta">{cuenta.code}</td>
                  <td className="balanza-desc">
                    <div className="balanza-desc-inner" style={{ paddingLeft: `${indentPx}px` }}>
                      <span>{name}</span>
                      {!isMayor && (
                        <Button
                          size="sm"
                          variant="link"
                          className="balanza-mov-btn text-decoration-none"
                          onClick={() => toggleMovimientos(cuenta)}
                        >
                          {expandedMovimientos[cuenta.id] ? '▾ Movs.' : '▸ Movs.'}
                        </Button>
                      )}
                    </div>
                  </td>
                  <td className="balanza-num">{formatBalanzaAmount(cuenta.saldo_inicial || 0)}</td>
                  <td className="balanza-num">{formatBalanzaAmount(cuenta.debe || 0)}</td>
                  <td className="balanza-num">{formatBalanzaAmount(cuenta.haber || 0)}</td>
                  <td className="balanza-num">{formatBalanzaAmount(cuenta.saldo_final || 0)}</td>
                </tr>,
              ]
              const movRow = renderMovimientosRow(cuenta)
              if (movRow) rows.push(movRow)
              return rows
            })}
          </tbody>
          {totales && (
            <tfoot>
              <tr>
                <td colSpan={2}>
                  <div className="balanza-totals-meta">
                    <span>{totalCuentas} Cuentas reportadas</span>
                    <span className="balanza-totales-label">Totales:</span>
                  </div>
                </td>
                <td className="balanza-num">{formatBalanzaAmount(totales.saldo_inicial || 0)}</td>
                <td className="balanza-num">{formatBalanzaAmount(totales.debe || 0)}</td>
                <td className="balanza-num">{formatBalanzaAmount(totales.haber || 0)}</td>
                <td className="balanza-num">{formatBalanzaAmount(totales.saldo_final || 0)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="balanza-report-footer">
        <span>Usuario: {usuarioLabel}</span>
        <span className="balanza-footer-center">| Fecha y hora: {impresionFecha}</span>
      </div>

      <Modal show={xmlModal.show} onHide={() => setXmlModal({ show: false, loading: false, data: null, error: '' })} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>XML de la transacción</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {xmlModal.loading ? (
            <div className="py-2"><Spinner size="sm" /> Cargando metadatos...</div>
          ) : xmlModal.error ? (
            <Alert variant="danger">{xmlModal.error}</Alert>
          ) : xmlModal.data ? (
            <div>
              <p><strong>Transacción:</strong> {xmlModal.data.transaction_id}</p>
              <p><strong>Referencia:</strong> {xmlModal.data.reference || '—'}</p>
              <p><strong>Tipo documento:</strong> {xmlModal.data.xml_tipo || '—'}</p>
              <p><strong>UUID:</strong> {xmlModal.data.uuid || '—'}</p>
              <p><strong>Archivo:</strong> {xmlModal.data.filename || '—'}</p>
              <p>
                <strong>Estatus XML:</strong>{' '}
                {xmlModal.data.has_xml ? <Badge bg="success">Disponible</Badge> : <Badge bg="secondary">No disponible</Badge>}
              </p>
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setXmlModal({ show: false, loading: false, data: null, error: '' })}>
            Cerrar
          </Button>
          <Button
            variant="primary"
            onClick={() => downloadXml(xmlModal.data?.transaction_id)}
            disabled={!xmlModal.data?.has_xml}
          >
            Descargar XML
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default AccountDrilldownTable
